import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DeliverableStepComponent } from '../deliverable-step.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SelectItem } from 'primeng/components/common/selectitem';
import { PlanCoordinate } from '../../../services/plan-coordinate';
import { PlanStepPage } from '../../../services/PlanStepPage';
import { IImplementationPlanInitiative } from '../../../services/IImplementationPlanInitiative';
import { EditOverlayComponent } from '../edit-overlay/edit-overlay.component';
import { PlanStep } from '../../../services/PlanStep';
import { DataUtils } from '../../../../shared/utilities/data-utils';
import { IDeliverableHeadings } from '../../../services/IDeliverableHeadings';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { StrategiesDataStoreService } from '../../../../shared/services/strategies-data-store.service';
import { StrtHasFlags } from '../../../../shared/models/ui/StrtHasFlags';
import { PriorityService } from '../../../services/priority.service';
import { Subscription } from 'rxjs/index';
import { StrtPriority } from '../../../../shared/models/plan/StrtPriority';
import {ResourceService} from '../../../services/resource.service';
import {StrtInitiative} from '../../../../shared/models/plan/StrtInitiative';
import {PriorityDropdownOption} from '../../../../shared/models/IPriorityDropdownOption';

@Component({
  selector: 'app-implementation-plan',
  templateUrl: './implementation-plan.component.html',
  styleUrls: ['./implementation-plan.component.scss']
})
export class ImplementationPlanComponent extends DeliverableStepComponent implements OnInit, OnDestroy {
  @ViewChild(EditOverlayComponent) editOverlayComp: EditOverlayComponent;
  @Input() headings: IDeliverableHeadings;
  @Input() strategyId: number;
  @Output() requestInitiatives = new EventEmitter<{startDate: Date, endDate?: Date}>();
  @Output() requestInitiativeDetails = new EventEmitter<{initiativeId: number}>();
  @Output() planCoordinateChange = new EventEmitter<PlanCoordinate>();
  private _hasFlags: StrtHasFlags = JSON.parse(JSON.stringify(this._strtDataStore.defaultHasFlags));
  private _selectedInitiative: IImplementationPlanInitiative;
  private _priorities: StrtPriority[] = [];

  private _implementationPlanForm: FormGroup;
  private _selectedInitiativeId: number;
  private _initiativeLoaded: boolean;
  private _initiativesAreBeingLoaded: boolean;
  private formTouched = false;
  private formSubmitted = false;
  private _allSubscriptions: Subscription[] = [];
  private readonly STATUS_VALUE_MAP = {
    'ON_TRACK': 'On Track',
    'LAGGING': 'Lagging',
    'AT_RISK': 'At Risk'
  };

  private readonly STATUS_COLOR_MAP = {
    'ON_TRACK': 'green',
    'LAGGING': 'yellow',
    'AT_RISK': 'red'
  };

  preventCalendarBlur = false;
  dropdownPriorityInitiatives: PriorityDropdownOption[] = [];
  availableInitiatives: SelectItem[] = [];
  startDate?: Date;
  endDate?: Date;

  constructor(private formBuilder: FormBuilder,
              protected cdr: ChangeDetectorRef,
              private _strtDataStore: StrategiesDataStoreService,
              private _domSanitize: DomSanitizer,
              private _priorityService: PriorityService,
              _resourceService: ResourceService) {
    super(PlanCoordinate.stepPageBy(PlanStepPage.ImplementationPlan), cdr, _resourceService);
    this.createForm();
  }

  get implementationPlanForm() {
    return this._implementationPlanForm;
  }

  get noInitiativesError() {
    return this._implementationPlanForm.dirty &&
      this.hasNoAvailableInitiatives &&
      !this._initiativesAreBeingLoaded &&
      this.datesAreValid(this.startDate, this.endDate);
  }

  ngOnInit() {
    this._initiativeLoaded = false;
    this._strtDataStore.datedInitiativesWithActivities = [];
    this._allSubscriptions.push(this._strtDataStore.datedInitiativesWithActivities$.subscribe(this.prioritiesHandler.bind(this)));
    this._allSubscriptions.push(this._strtDataStore.hasFlags$.subscribe(this.hasFlagsHandler.bind(this)));
  }

  ngOnDestroy(): void {
    if (this._allSubscriptions && this._allSubscriptions.length) {
      this._allSubscriptions.forEach((subscription: Subscription) => {
        subscription.unsubscribe();
      });
    }
    this.reset();
  }

  get hasFlags() {
    return this._hasFlags;
  }

  hasFlagsHandler(hasFlags: StrtHasFlags) {
    this._hasFlags = hasFlags;
    if (this.startDateField) {
      this._hasFlags.hasInitiatives ? this.startDateField.enable() : this.startDateField.disable();
    }
    if (this.endDateField) {
      this._hasFlags.hasInitiatives ? this.endDateField.enable() : this.endDateField.disable();
    }
  }

  prioritiesHandler(priorities: StrtPriority[]) {
    this._priorities = priorities;
    this.availableInitiatives = DataUtils.reshapePriorities(this._priorities);
    this.dropdownPriorityInitiatives = PriorityDropdownOption.fromPriorityInitiatives(this._priorities);
    this.updateDropdownDisablement();
    this._initiativesAreBeingLoaded = false;
  }

  updateDropdownDisablement() {
    if (this.hasNoAvailableInitiatives) {
      this.initiativeField.disable();
    } else {
      this.initiativeField.enable();
    }
  }

  onViewImplementationPlan() {
    this.formSubmitted = true;
    this.requestInitiativeDetails.emit({initiativeId: this._selectedInitiativeId});
  }

  @Input() set selectedInitiative(newInitiative: IImplementationPlanInitiative) {
    this._selectedInitiative = newInitiative;
    if (newInitiative.initiative !== '') {
      this._initiativeLoaded = true;
    }
  }

  onPlanCoordinateChangeCreatingObjectivesAndCriticalInitiatives() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.CREATE_OBJECTIVES_AND_CRITICAL_INITIATIVES));
  }

  onPlanCoordinateChangeDesignImplementationPlan() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.DESIGN_THE_IMPLEMENTATION_PLAN));
  }

  onPlanCoordinateChangeUpdateProgress() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.ENACT_AND_MONITOR_PROGRESS));
  }

  get calendarContainerClass() {
    if (this._hasFlags.hasInitiatives) {
      return 'calendar-container';
    } else {
      return 'calendar-container disabled';
    }
  }

  fetchInitiatives() {
    this._priorityService.getDatedInitiativesWithActivities(this.strategyId, this.startDate, this.endDate);
  }

  get selectedInitiative(): IImplementationPlanInitiative {
    return this._selectedInitiative;
  }

  get startDateField() {
    return this.implementationPlanForm.get('startDate');
  }

  get endDateField() {
    return this.implementationPlanForm.get('endDate');
  }

  get initiativeField() {
    return this.implementationPlanForm.get('selectedInitiative');
  }

  resetDropdownAndFetch() {
    this._initiativesAreBeingLoaded = true;
    this.resetDropdown();
    this.fetchInitiatives();
  }

  resetDropdown() {
    this.initiativeField.reset();
    this.availableInitiatives = [];
    this._initiativeLoaded = false;
    this._selectedInitiativeId = 0;
    this.updateDropdownDisablement();
  }

  onSelectInitiative(initiativeSelectEvent) {
    this._selectedInitiativeId = initiativeSelectEvent.value;
  }

  get isSubmitDisabled() {
    return !(this.implementationPlanForm.status === 'VALID' &&
      !this.implementationPlanForm.pristine &&
      this.implementationPlanForm.dirty);
  }

  get hasRequiredFields() {

    return this._selectedInitiativeId > 0;
  }

  private createForm() {
    const validatorArr = [
      Validators.required
    ];
    this._implementationPlanForm = this.formBuilder.group({
      startDate: [{value: this.startDate, disabled: true}, validatorArr],
      endDate: [{value: this.endDate, disabled: true}, []],
      selectedInitiative: [{value: this.selectedInitiative, disabled: true}, validatorArr]
    });
  }

  get hasNoAvailableInitiatives() {
    return !this.availableInitiatives ||  this.availableInitiatives.length === 0;
  }

  get hasSelectedInitiative() {
    return this._initiativeLoaded;
  }

  get hasActivities() {
    return this.selectedInitiative.activities.length > 0;
  }

  isLastActivity(activities: {}[], index: number) {
    return (activities.length - 1) === index;
  }

  addTitleBorder(activity: {measuresAndTargets: {measure?: string, target?: string, targetDate?: string}[]}) {
    return activity.measuresAndTargets.length !== 0;
  }

  isTargetAvailable(measureAndTarget: {measure?: string, target?: string, targetDate?: string}) {
    return measureAndTarget.target && measureAndTarget.target !== '';
  }

  isMeasureAvailable(measureAndTarget: {measure?: string, target?: string, targetDate?: string}) {
    return measureAndTarget.measure && measureAndTarget.measure !== '';
  }

  get hasEndDateWithNoStartDateAndTouched() {
    return this.endDate && !this.startDate && (this.formTouched || this.formSubmitted);
  }

  get isEndDateBeforeStartDate() {
    const startDate = this.startDateField.value,
          endDate = this.endDateField.value;
    return startDate !== null && startDate !== '' && endDate !== null && endDate !== '' && endDate < startDate;
  }

  reset() {
    this._initiativeLoaded = false;
    this._selectedInitiativeId = 0;
    this._implementationPlanForm.reset();
    this.formSubmitted = false;
    this.availableInitiatives = [];
    this._selectedInitiativeId = 0;
    this.formTouched = false;
    this.startDate = undefined;
    this.endDate = undefined;
    this.initiativeField.reset();
    this.updateDropdownDisablement();
  }

  get initiativeDropdownInlineStyles() {
    return {
      width : '280px',
      margin : 0,
      padding: '0 16px 0 8px',
      height: '38px',
      lineHeight: '30px',
      border : '1px solid #706F6F',
      borderRadius: '4px',
      fontSize: '14px'
    };
  }

  get statusButtonClass() {
    let output = 'btn btn-primary status-button';
    switch (this.selectedInitiative.status) {
      case 'On Track':
        output += ' on-track';
        break;
      case 'Lagging':
        output += ' lagging';
        break;
      case 'At Risk':
        output += ' at-risk';
        break;
    }
    return output;
  }

  get hasTrackingEnabled() {
    return this.selectedInitiative.trackingToolsEnabled;
  }

  get initiativeHasCitation(): boolean {
    return !!this.selectedInitiative.citation && this.selectedInitiative.citation.length > 0;
  }

  get safeCitation(): SafeHtml {
    return this._domSanitize.bypassSecurityTrustHtml(this.selectedInitiative.citation || '');
  }


  resetCalendarEventBlocker() {
    this.preventCalendarBlur = true;
  }

  allowCalendarBlurEvent() {
    this.preventCalendarBlur = false;
  }

  applyStartDate(event) {
    if (event && event.type && event.type === 'blur' && this.preventCalendarBlur) {
      // Allows us to prevent executing this code in an edge caused by p-calendar
      // The edge case is the onBlur event being fired when a user clicks a date
      // The onBlur gets fired before the select event, which ends up clearing out the date
      return;
    }
    this.startDate = this.startDateField.value;
    this.formTouched = true;
    if (this.datesAreValid(this.startDate, this.endDate)) {
      this.resetDropdownAndFetch();
    } else {
      this.resetDropdown();
    }
  }

  applyEndDate(event) {
    if (event && event.type && event.type === 'blur' && this.preventCalendarBlur) {
      // Allows us to prevent executing this code in an edge caused by p-calendar
      // The edge case is the onBlur event being fired when a user clicks a date
      // The onBlur gets fired before the select event, which ends up clearing out the date
      return;
    }
    this.endDate = this.endDateField.value;
    this.formTouched = true;
    if (this.datesAreValid(this.startDate, this.endDate)) {
      this.resetDropdownAndFetch();
    } else {
      this.resetDropdown();
    }
  }


  datesAreValid(startDate: Date, endDate: Date): boolean {
    if (startDate && endDate) {
      if (startDate > endDate) {
        return false;
      }
    } else if (endDate && !startDate) {
      return false;
    } else if (!startDate) {
      return false;
    }

    return true;
  }

  getInitiativeStatusValue(initiative: StrtInitiative): string {
    return this.STATUS_VALUE_MAP[initiative.initiativeStatus];
  }

  getInitiativeStatusColor(initiative: StrtInitiative): string {
    return this.STATUS_COLOR_MAP[initiative.initiativeStatus];
  }

  dropdownValueChanged(event) {
    this.onSelectInitiative(event);
  }

}
